import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import RefreshIcon from '@material-ui/icons/Refresh';
import { useStyles } from './Styles';
import GanttGrid from './GanttGrid';
import {
  ROW_KIND,
  ZOOM,
  allExpandableIds,
  buildRowTree,
  unmappedStatuses,
} from './ganttModel';
import {
  fetchGanttCmoOptionsAPI,
  fetchGanttPortfolioAPI,
  fetchGanttSiteOptionsAPI,
} from './api';

/**
 * Gantt Chart (Gantt Chart - Functional Specs).
 *
 * A single interactive timeline of manufacturing activity across the campaign
 * portfolio, with two navigation levels:
 *   Portfolio View  — every Project, quarter-scaled and auto-fit (BR-01)
 *   Campaign View   — one Campaign's Stages and Batches, Month or Week scale
 *
 * This is an in-app full-screen view (not a routed page / no URL), opened and
 * closed by ProductDevelopment through ProjectDevNavContext — the same pattern
 * Batch Analytics and Batch Genealogy already use, so the browser URL never
 * changes and the dashboard's state survives underneath.
 *
 * Composition:
 *   ./ganttModel — every rule (status normalisation, rollups, colour matrix,
 *                  timeline maths). Pure, unit-testable.
 *   ./GanttGrid  — the drawing (header bands, grid, bars, today marker, tooltip)
 *   this file    — the screen: facility selectors, breadcrumb, zoom, status
 *                  chips, expand/collapse, and the two navigation levels
 */

// Portfolio View offers Quarter + Month; Campaign View adds Week (FSD C, BR-03).
const PORTFOLIO_ZOOMS = [ZOOM.QUARTER, ZOOM.MONTH];
const CAMPAIGN_ZOOMS = [ZOOM.QUARTER, ZOOM.MONTH, ZOOM.WEEK];

const GanttChart = ({
  onBack, showProgress, focusProjectLabel, initialCmoOrgId, initialSite,
}) => {
  const classes = useStyles();

  // ── facility selection (FSD A) ────────────────────────────────────────────
  const [cmoOptions, setCmoOptions] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);
  // FSD A: CMO defaults to the sponsor's current CDMO and Site to that CDMO's
  // first site. Empty until the option lists arrive -- there is no "All" choice
  // to start on, so the fields are briefly blank rather than claiming a scope
  // that was never offered.
  const [cmo, setCmo] = useState('');
  const [site, setSite] = useState('');

  // ── data ──────────────────────────────────────────────────────────────────
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastSynced, setLastSynced] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  // ── view state ────────────────────────────────────────────────────────────
  // null = Portfolio View; otherwise the drilled-into campaign's row id.
  const [campaignRowId, setCampaignRowId] = useState(null);
  const [zoom, setZoom] = useState(ZOOM.QUARTER);
  // Expansion is tracked per row id and kept SEPARATELY for the two navigation
  // levels (FSD F Business Rules: "independent between the two navigation
  // levels"), so returning to Portfolio View does not inherit the campaign
  // view's open stages and vice versa.
  const [portfolioExpanded, setPortfolioExpanded] = useState(() => new Set());
  const [campaignExpanded, setCampaignExpanded] = useState(() => new Set());

  // Fetch the facility option lists once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await fetchGanttCmoOptionsAPI();
      if (cancelled) return;
      const options = res.data || [];
      setCmoOptions(options);
      // The chart opens on whatever the dashboard was already scoped to, so the
      // user does not have to re-pick what they had just chosen. Only when the
      // dashboard was on "All" -- or on something this chart does not offer --
      // does FSD A's default apply: "the sponsor's current CDMO (currently 'Sai
      // Life Sciences')", which is the first entry fetchCMOMembers returns for
      // this sponsor, so taking it from the list rather than matching the name
      // keeps it right for any other sponsor.
      if (options.length) {
        const carried = options.some((o) => String(o.id) === String(initialCmoOrgId))
          ? String(initialCmoOrgId)
          : null;
        setCmo((prev) => prev || carried || options[0].id);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Site options depend on the selected CMO, so they reload with it — and the
  // site selection resets, since a site from another CMO is not a valid choice.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!cmo) return;
      const res = await fetchGanttSiteOptionsAPI(cmo);
      if (cancelled) return;
      const options = res.data || [];
      setSiteOptions(options);
      // Same as the CMO above: the dashboard's site carries over when this CDMO
      // offers it, otherwise FSD A's default applies -- "defaults to 'HYD / Unit
      // II' and offers 'Bidar / Unit IV' as the alternate option", which are the
      // selected CDMO's own sites, so listing them and taking the first gives
      // the FSD's result without naming either in code.
      const carriedSite = options.some((o) => String(o.id) === String(initialSite))
        ? String(initialSite)
        : null;
      setSite(carriedSite || (options.length ? options[0].id : ''));
    })();
    return () => { cancelled = true; };
  }, [cmo]);

  // Portfolio data. BR-12 says changing Site does not re-query in this release
  // — that is a statement about the prototype's single static data source, not
  // something to reproduce here: the dashboard feed is genuinely scopeable by
  // CMO and Site, so the chart re-queries and shows the scope the user picked.
  // Doing otherwise would leave the selectors visibly lying about the data.
  useEffect(() => {
    let cancelled = false;
    // The CDMO is not known until its option list arrives. Querying with an
    // empty one would fetch the whole portfolio and then re-fetch a moment
    // later, so the chart waits one tick instead of flashing the wrong scope.
    if (!cmo) return undefined;
    setLoading(true);
    (async () => {
      // BR-12 / section A: "the underlying REAL_DATA source is not re-fetched on
      // Site change (single active data source per session)" — Site is a
      // presentation field only, so it is not sent and does not re-query.
      const res = await fetchGanttPortfolioAPI({ cmoOrgId: cmo });
      if (cancelled) return;
      // A status the table does not know -- or a batch carrying no status at
      // all -- is drawn as Planned grey, which reads as "nothing is known about
      // this row" (FSD K). Either way real work is understated, so name them
      // rather than let a stage go quietly grey.
      const unmapped = unmappedStatuses(res.projects);
      if (unmapped.length) {
        console.warn('[GanttChart] Batch statuses drawn as Planned grey:', unmapped);
      }
      setProjects(res.projects || []);
      setLastSynced(new Date());
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [cmo, reloadToken]);

  // ── derived rows ──────────────────────────────────────────────────────────
  const fullTree = useMemo(() => buildRowTree(projects), [projects]);

  // FSD D / BR-06: the status filter chips were retired post-feedback, so
  // there is nothing to filter by — every row inside the selected CMO / Site
  // scope is displayed. Kept as a named binding rather than renaming every
  // use, so the diff stays reviewable.
  const filteredTree = fullTree;

  // The drilled campaign row.
  const campaignRow = useMemo(() => {
    if (!campaignRowId) return null;
    for (const project of filteredTree) {
      for (const campaign of project.children || []) {
        if (campaign.id === campaignRowId) return { ...campaign, projectLabel: project.name };
      }
    }
    return null;
  }, [filteredTree, campaignRowId]);

  // A campaign that filtering has removed entirely can no longer be shown —
  // fall back to Portfolio View rather than rendering an empty campaign.
  useEffect(() => {
    if (campaignRowId && !campaignRow) setCampaignRowId(null);
  }, [campaignRowId, campaignRow]);

  // Rows handed to the grid: at Portfolio View the Project tree; in Campaign
  // View the selected campaign's Stage rows (FSD G).
  const gridRows = campaignRow ? campaignRow.children : filteredTree;
  const expanded = campaignRow ? campaignExpanded : portfolioExpanded;

  // Campaign View defaults its Stage rows to expanded (FSD G / BR-11), so only
  // Batch-level detail is toggled there.
  useEffect(() => {
    if (!campaignRow) return;
    setCampaignExpanded(new Set((campaignRow.children || []).map((s) => s.id)));
  }, [campaignRowId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── navigation ────────────────────────────────────────────────────────────
  const drillIntoCampaign = useCallback((row) => {
    if (!row || row.kind !== ROW_KIND.CAMPAIGN) return; // only Campaigns drill (BR-02)
    setCampaignRowId(row.id);
    // Campaign View opens at Month (FSD G Business Rules).
    setZoom(ZOOM.MONTH);
  }, []);

  const backToPortfolio = useCallback(() => {
    setCampaignRowId(null);
    // FSD B: returning to Portfolio View resets zoom to Quarter and clears
    // row-expansion state.
    setZoom(ZOOM.QUARTER);
    setPortfolioExpanded(new Set());
  }, []);

  const toggleExpand = useCallback((id) => {
    const apply = (prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    };
    if (campaignRow) setCampaignExpanded(apply); else setPortfolioExpanded(apply);
  }, [campaignRow]);

  // Opened from a project row's "View Gantt Chart": start with that project
  // open, so the user lands on the chart they picked instead of a collapsed
  // portfolio (product, 2026-10-01). Runs once per tree: a later collapse by
  // the user is not undone. With no selection the portfolio stays collapsed,
  // which is BR-01's default landing view.
  const focusApplied = useRef(null);
  useEffect(() => {
    if (!focusProjectLabel || !filteredTree.length) return;
    const row = filteredTree.find((r) => r.name === focusProjectLabel);
    if (!row || focusApplied.current === row.id) return;
    focusApplied.current = row.id;
    setPortfolioExpanded((prev) => new Set(prev).add(row.id));
  }, [focusProjectLabel, filteredTree]);

  const expandAll = useCallback(() => {
    setPortfolioExpanded(new Set(allExpandableIds(filteredTree)));
  }, [filteredTree]);
  const collapseAll = useCallback(() => setPortfolioExpanded(new Set()), []);

  const zoomOptions = campaignRow ? CAMPAIGN_ZOOMS : PORTFOLIO_ZOOMS;
  // Leaving Campaign View while Week is selected would leave Portfolio View on
  // a zoom it does not offer (BR-03), so fall back to Quarter.
  useEffect(() => {
    if (!campaignRow && zoom === ZOOM.WEEK) setZoom(ZOOM.QUARTER);
  }, [campaignRow, zoom]);

  return (
    <div className={classes.container}>
      {/* ── header ── */}
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <div className={classes.backButton} onClick={onBack}>
            <ChevronLeftIcon />
            <Typography variant="body1">Back</Typography>
          </div>
          <Typography className={classes.title}>Gantt Chart</Typography>
        </div>
      </div>

      {/* ── controls: CMO / Site + Last Synced / Refresh (FSD A) above the
          zoom, status chips and expand/collapse strip ── */}
      <div className={classes.controlPanel}>
        {/* BR-13: a single compact ribbon row — CMO, Site / Plant, Zoom,
            Last Synced / Refresh, Expand all / Collapse all. It used to be two
            stacked rows, and carried a status chip row and a legend that FSD
            section D retired. */}
        <div className={classes.facilityRow}>
          {/* FSD B: a second way back to Portfolio View while in Campaign view. */}
          {campaignRow && (
            <Button className={classes.textButton} onClick={backToPortfolio}>
              ← All Projects
            </Button>
          )}
          <div className={classes.fieldRow}>
            <span className={classes.fieldLabel}>CMO:</span>
            <FormControl variant="outlined" size="small" className={classes.field}>
              {/* FSD A: a single-item selector -- the sponsor's CDMO, with no
                  "All CMOs" entry, since the chart is always scoped to one. */}
              <Select value={cmo} onChange={(e) => setCmo(e.target.value)} displayEmpty>
                {cmoOptions.map((o) => (<MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>))}
              </Select>
            </FormControl>
          </div>
          <div className={classes.fieldRow}>
            <span className={classes.fieldLabel}>Site / Plant:</span>
            <FormControl variant="outlined" size="small" className={classes.field}>
              {/* FSD A: the selected CDMO's sites only -- one default and its
                  alternates, with no "All sites" entry. */}
              <Select value={site} onChange={(e) => setSite(e.target.value)} displayEmpty>
                {siteOptions.map((o) => (<MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>))}
              </Select>
            </FormControl>
          </div>
          <div className={classes.toolbarGroup}>
            <span className={classes.toolbarLabel}>Zoom</span>
            {zoomOptions.map((z) => (
              <button
                type="button"
                key={z}
                className={`${classes.zoomButton} ${zoom === z ? classes.zoomButtonActive : ''}`}
                onClick={() => setZoom(z)}
              >
                {z}
              </button>
            ))}
          </div>

          <div className={classes.syncWrap}>
            <span className={classes.syncText}>
              {lastSynced
                ? `Last Synced @ ${moment(lastSynced).format('DD-MMM-YYYY HH:mm:ss').toUpperCase()}`
                : 'Syncing…'}
            </span>
            <Button
              className={classes.refreshButton}
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={() => setReloadToken((n) => n + 1)}
              disabled={loading}
            >
              Refresh
            </Button>
          </div>


          {/* FSD E / BR-11 / BR-13: Portfolio View only, outlined pill
              buttons at the far right of the ribbon. */}
          {!campaignRow && (
            <div className={classes.toolbarGroup} style={{ marginLeft: 'auto' }}>
              <Button variant="outlined" className={classes.pillButton} onClick={expandAll}>
                Expand all
              </Button>
              <Button variant="outlined" className={classes.pillButton} onClick={collapseAll}>
                Collapse all
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* ── breadcrumb (FSD B) — Portfolio View shows none ── */}
      {campaignRow && (
        <div className={classes.breadcrumb}>
          <button type="button" className={classes.crumbLink} onClick={backToPortfolio}>
            {campaignRow.projectLabel}
          </button>
          <span className={classes.crumbSep}>›</span>
          <span className={classes.crumbCurrent}>{campaignRow.name}</span>
        </div>
      )}

      {/* ── chart ── */}
      {loading ? (
        <div className={classes.state}>
          <CircularProgress size={22} />
          <div style={{ marginTop: 12 }}>Loading Gantt Chart…</div>
        </div>
      ) : !fullTree.length ? (
        <div className={classes.state}>
          No dated manufacturing activity for this CMO / site.
        </div>
      ) : (
        <>
          <GanttGrid
            rows={gridRows}
            zoom={zoom}
            expanded={expanded}
            onToggleExpand={toggleExpand}
            onDrillCampaign={drillIntoCampaign}
            campaignRow={campaignRow}
            showProgress={showProgress}
          />
        </>
      )}
    </div>
  );
};

GanttChart.propTypes = {
  // The dashboard's current CMO / Site, so the chart opens on the same scope.
  initialCmoOrgId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialSite: PropTypes.string,
  // Closes the view and returns to the dashboard.
  onBack: PropTypes.func.isRequired,
  // FSD I / BR-09: the in-progress elapsed fill is suppressed entirely when
  // Show Progress is off for the dashboard instance.
  showProgress: PropTypes.bool,
  // The project display label to open on, when the chart was opened from a
  // dashboard row rather than from the toolbar.
  focusProjectLabel: PropTypes.string,
};

GanttChart.defaultProps = {
  showProgress: true,
  focusProjectLabel: null,
};

export default GanttChart;
